import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowDownTrayIcon, MagnifyingGlassIcon, ArrowRightIcon, ArrowPathIcon } from '@heroicons/react/24/solid';
import { Article } from '../data/articles';
import { fetchArticles } from '../api/articles';
import { dataFileUrl } from '../constant/hostname';

const formatDate = (date: string) =>
  `${date.slice(0, 4)}/${date.slice(4, 6)}/${date.slice(6, 8)}`;

const ArticleList: React.FC = () => {
  const [articles, setArticles] = useState<Article[]>([]);
  const [loadState, setLoadState] = useState<'loading' | 'error' | 'ready'>('loading');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const articlesPerPage: number = 6;

  useEffect(() => {
    let cancelled = false;
    setLoadState('loading');
    fetchArticles()
      .then((data) => {
        if (cancelled) return;
        setArticles(data);
        setLoadState('ready');
      })
      .catch(() => {
        if (cancelled) return;
        setLoadState('error');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const sortedArticles = [...articles].sort(
    (a: Article, b: Article) => b.createdAt.localeCompare(a.createdAt)
  );

  const filteredArticles = sortedArticles.filter(article => {
    const q = searchQuery.toLowerCase();
    return article.title.toLowerCase().includes(q) || article.author.toLowerCase().includes(q);
  });

  const totalPages: number = Math.ceil(filteredArticles.length / articlesPerPage);
  const currentArticles: Article[] = filteredArticles.slice(
    (currentPage - 1) * articlesPerPage,
    currentPage * articlesPerPage
  );

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  return (
    <section className="w-full flex flex-col items-center">
      <div className="w-full max-w-6xl">
        {/* セクションヘッダー */}
        <div className="text-center mb-8 sm:mb-12">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-2">
            記事一覧
          </h2>
          <p className="text-gray-500 text-base">Articles</p>
        </div>

        {/* 検索バー */}
        <div className="max-w-xl mx-auto mb-8 sm:mb-12">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
            <input
              type="text"
              placeholder="タイトル・著者で検索..."
              value={searchQuery}
              onChange={handleSearch}
              className="w-full pl-11 pr-4 h-11 sm:h-12 rounded-full border border-gray-200 bg-white text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-[#4a7c59]/40 focus:border-[#4a7c59] transition"
            />
          </div>
        </div>

        {loadState === 'loading' && (
          <div className="text-center py-16 text-gray-400 flex flex-col items-center gap-3">
            <ArrowPathIcon className="h-6 w-6 animate-spin text-[#4a7c59]" />
            <p className="text-base">記事を読み込んでいます...</p>
          </div>
        )}

        {loadState === 'error' && (
          <div className="text-center py-16 text-gray-400">
            <p className="text-base">記事を読み込めませんでした。</p>
          </div>
        )}

        {loadState === 'ready' && currentArticles.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-8 sm:mb-12">
            {currentArticles.map((article) => {
              const pdfHref = dataFileUrl(article.url);
              return (
              <article
                key={article.id}
                className="bg-white rounded-xl sm:rounded-2xl p-5 sm:p-6 border border-gray-200/60 hover:shadow-lg transition-all group flex flex-col"
              >
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs text-gray-400">{formatDate(article.createdAt)}</span>
                  {pdfHref && (
                    <a
                      href={pdfHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="PDFをダウンロード"
                      className="text-gray-400 hover:text-[#4a7c59] transition"
                      onClick={e => e.stopPropagation()}
                    >
                      <ArrowDownTrayIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                    </a>
                  )}
                </div>
                <h3 className="font-bold text-sm sm:text-base text-gray-900 mb-1 line-clamp-2 group-hover:text-[#1a4a2e] transition-colors leading-snug">
                  {article.title}
                </h3>
                <p className="text-xs sm:text-sm text-[#4a7c59] font-medium mb-2">
                  {article.author}
                </p>
                <p className="text-xs sm:text-sm text-gray-500 mb-4 line-clamp-3 leading-relaxed flex-1">
                  {article.abstract}
                </p>
                <Link
                  to={`/articles/${encodeURIComponent(article.id)}`}
                  className="inline-flex items-center gap-1 text-xs sm:text-sm font-medium text-[#1a4a2e] hover:underline mt-auto"
                >
                  詳細を見る
                  <ArrowRightIcon className="w-3 h-3" />
                </Link>
              </article>
              );
            })}
          </div>
        )}

        {loadState === 'ready' && currentArticles.length === 0 && (
          <div className="text-center py-16 text-gray-400">
            <p className="text-base">該当する記事が見つかりませんでした。</p>
          </div>
        )}

        {/* ページネーション */}
        {loadState === 'ready' && totalPages > 1 && (
          <div className="flex flex-wrap justify-center gap-2">
            <button
              className={`w-9 h-9 sm:w-10 sm:h-10 rounded-full border text-sm transition flex items-center justify-center ${currentPage === 1 ? 'text-gray-300 border-gray-200 cursor-not-allowed' : 'text-[#1a4a2e] border-[#1a4a2e]/30 hover:bg-[#f5f0e8]'}`}
              onClick={() => setCurrentPage(p => Math.max(p - 1, 1))}
              disabled={currentPage === 1}
            >‹</button>
            {[...Array(totalPages)].map((_, i) => (
              <button
                key={i}
                className={`w-9 h-9 sm:w-10 sm:h-10 rounded-full border text-sm transition ${currentPage === i + 1 ? 'bg-[#1a4a2e] text-white border-[#1a4a2e]' : 'text-[#1a4a2e] border-[#1a4a2e]/30 hover:bg-[#f5f0e8]'}`}
                onClick={() => setCurrentPage(i + 1)}
              >
                {i + 1}
              </button>
            ))}
            <button
              className={`w-9 h-9 sm:w-10 sm:h-10 rounded-full border text-sm transition flex items-center justify-center ${currentPage === totalPages ? 'text-gray-300 border-gray-200 cursor-not-allowed' : 'text-[#1a4a2e] border-[#1a4a2e]/30 hover:bg-[#f5f0e8]'}`}
              onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))}
              disabled={currentPage === totalPages}
            >›</button>
          </div>
        )}
      </div>
    </section>
  );
};

export default ArticleList;
